/**
 * Date period definitions and resolution — maps DatePeriodId + value to concrete date ranges.
 *
 * SOURCE: ENTITY_MODULE_EXPORT/lib/utils/datePeriods.ts
 * Adapted for Commission Manager: @/ path aliases replaced with relative imports.
 */

import { addDays, startOfDay, endOfDay, toLocalISOString } from "./datePresets";
import type { DatePeriodId, FilterRuleValue } from "../types/filters";

// ── DatePeriodValueShape ──────────────────────────────────────────────────────

export type DatePeriodValueShape =
  | "none"
  | "n_days"
  | "exact_date"
  | "month_year"
  | "quarter_year"
  | "year_only"
  | "date_range";

// ── DatePeriodDef ─────────────────────────────────────────────────────────────

export type DatePeriodGroup = "common" | "rolling" | "relative_n" | "specific" | "range";

export interface DatePeriodDef {
  id: DatePeriodId;
  label: string;
  group: DatePeriodGroup;
  valueShape: DatePeriodValueShape;
}

// ── DATE_PERIOD_DEFS ──────────────────────────────────────────────────────────

export const DATE_PERIOD_DEFS: Record<DatePeriodId, DatePeriodDef> = {
  NONE: { id: "NONE", label: "Any date", group: "common", valueShape: "none" },
  CURRENT_DAY: { id: "CURRENT_DAY", label: "Current day", group: "common", valueShape: "none" },
  YESTERDAY: { id: "YESTERDAY", label: "Yesterday", group: "common", valueShape: "none" },
  TOMORROW: { id: "TOMORROW", label: "Tomorrow", group: "common", valueShape: "none" },
  CURRENT_WEEK: { id: "CURRENT_WEEK", label: "This week", group: "rolling", valueShape: "none" },
  CURRENT_MONTH: { id: "CURRENT_MONTH", label: "This month", group: "rolling", valueShape: "none" },
  CURRENT_QUARTER: {
    id: "CURRENT_QUARTER",
    label: "Current quarter",
    group: "rolling",
    valueShape: "none",
  },
  LAST_7_DAYS: { id: "LAST_7_DAYS", label: "Last 7 days", group: "rolling", valueShape: "none" },
  LAST_30_DAYS: { id: "LAST_30_DAYS", label: "Last 30 days", group: "rolling", valueShape: "none" },
  LAST_60_DAYS: { id: "LAST_60_DAYS", label: "Last 60 days", group: "rolling", valueShape: "none" },
  LAST_90_DAYS: { id: "LAST_90_DAYS", label: "Last 90 days", group: "rolling", valueShape: "none" },
  LAST_WEEK: { id: "LAST_WEEK", label: "Last week", group: "rolling", valueShape: "none" },
  LAST_MONTH: { id: "LAST_MONTH", label: "Last month", group: "rolling", valueShape: "none" },
  NEXT_WEEK: { id: "NEXT_WEEK", label: "Next week", group: "rolling", valueShape: "none" },
  NEXT_MONTH: { id: "NEXT_MONTH", label: "Next month", group: "rolling", valueShape: "none" },
  PREV_DAYS: { id: "PREV_DAYS", label: "Last N days", group: "relative_n", valueShape: "n_days" },
  NEXT_DAYS: { id: "NEXT_DAYS", label: "Next N days", group: "relative_n", valueShape: "n_days" },
  EXACT: { id: "EXACT", label: "Exact date", group: "specific", valueShape: "exact_date" },
  MONTH: { id: "MONTH", label: "Month", group: "specific", valueShape: "month_year" },
  QUARTER: { id: "QUARTER", label: "Quarter", group: "specific", valueShape: "quarter_year" },
  YEAR: { id: "YEAR", label: "Year", group: "specific", valueShape: "year_only" },
  RANGE: { id: "RANGE", label: "Custom range", group: "range", valueShape: "date_range" },
};

// ── Group display metadata ────────────────────────────────────────────────────

export const DATE_PERIOD_GROUP_LABELS: Record<DatePeriodGroup, string> = {
  common: "Common",
  rolling: "Rolling periods",
  relative_n: "Last / Next N days",
  specific: "Specific period",
  range: "Date range",
};

// ── Type guard ────────────────────────────────────────────────────────────────

const DATE_PERIOD_ID_SET = new Set<string>(Object.keys(DATE_PERIOD_DEFS));

export function isDatePeriodOperator(op: string): op is DatePeriodId {
  return DATE_PERIOD_ID_SET.has(op);
}

// ── Internal date math helpers ────────────────────────────────────────────────

function mondayOfWeek(d: Date): Date {
  const day = d.getDay();
  const offset = day === 0 ? -6 : 1 - day;
  return addDays(d, offset);
}

function firstOfMonth(year: number, month: number): Date {
  return new Date(year, month, 1, 0, 0, 0, 0);
}

function lastOfMonth(year: number, month: number): Date {
  return endOfDay(new Date(year, month + 1, 0));
}

function firstOfQuarter(year: number, q: number): Date {
  return new Date(year, (q - 1) * 3, 1, 0, 0, 0, 0);
}

function lastOfQuarter(year: number, q: number): Date {
  return endOfDay(new Date(year, (q - 1) * 3 + 3, 0));
}

function currentQuarterNum(month: number): 1 | 2 | 3 | 4 {
  return (Math.floor(month / 3) + 1) as 1 | 2 | 3 | 4;
}

interface DateRange {
  start: Date;
  end: Date;
}
function r(start: Date, end: Date): DateRange {
  return { start, end };
}

// ── resolveDatePeriodToRange ──────────────────────────────────────────────────

/**
 * Resolves a DatePeriodId + its stored value to a concrete { start, end } range.
 *
 * Returns null for NONE (any date — no filter condition emitted).
 * All times are local (no UTC shifting).
 */
export function resolveDatePeriodToRange(
  id: DatePeriodId,
  value: FilterRuleValue,
  now: Date,
): DateRange | null {
  switch (id) {
    case "NONE":
      return null;

    case "CURRENT_DAY":
      return r(startOfDay(now), endOfDay(now));

    case "YESTERDAY": {
      const d = addDays(now, -1);
      return r(startOfDay(d), endOfDay(d));
    }

    case "TOMORROW": {
      const d = addDays(now, 1);
      return r(startOfDay(d), endOfDay(d));
    }

    case "CURRENT_WEEK": {
      const mon = mondayOfWeek(now);
      return r(startOfDay(mon), endOfDay(addDays(mon, 6)));
    }

    case "CURRENT_MONTH":
      return r(
        firstOfMonth(now.getFullYear(), now.getMonth()),
        lastOfMonth(now.getFullYear(), now.getMonth()),
      );

    case "CURRENT_QUARTER": {
      const q = currentQuarterNum(now.getMonth());
      return r(firstOfQuarter(now.getFullYear(), q), lastOfQuarter(now.getFullYear(), q));
    }

    case "LAST_7_DAYS":
      return r(startOfDay(addDays(now, -7)), endOfDay(now));

    case "LAST_30_DAYS":
      return r(startOfDay(addDays(now, -30)), endOfDay(now));

    case "LAST_60_DAYS":
      return r(startOfDay(addDays(now, -60)), endOfDay(now));

    case "LAST_90_DAYS":
      return r(startOfDay(addDays(now, -90)), endOfDay(now));

    case "LAST_WEEK": {
      const thisMon = mondayOfWeek(now);
      const lastMon = addDays(thisMon, -7);
      return r(startOfDay(lastMon), endOfDay(addDays(lastMon, 6)));
    }

    case "LAST_MONTH": {
      const y = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
      const m = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
      return r(firstOfMonth(y, m), lastOfMonth(y, m));
    }

    case "NEXT_WEEK": {
      const thisMon = mondayOfWeek(now);
      const nextMon = addDays(thisMon, 7);
      return r(startOfDay(nextMon), endOfDay(addDays(nextMon, 6)));
    }

    case "NEXT_MONTH": {
      const y = now.getMonth() === 11 ? now.getFullYear() + 1 : now.getFullYear();
      const m = now.getMonth() === 11 ? 0 : now.getMonth() + 1;
      return r(firstOfMonth(y, m), lastOfMonth(y, m));
    }

    case "PREV_DAYS": {
      const n = isNDaysValue(value) ? value.n : 7;
      return r(startOfDay(addDays(now, -n)), endOfDay(addDays(now, -1)));
    }

    case "NEXT_DAYS": {
      const n = isNDaysValue(value) ? value.n : 7;
      return r(startOfDay(addDays(now, 1)), endOfDay(addDays(now, n)));
    }

    case "EXACT": {
      const iso = typeof value === "string" ? value : "";
      if (!iso) return r(startOfDay(now), endOfDay(now));
      const d = new Date(iso);
      if (isNaN(d.getTime())) return r(startOfDay(now), endOfDay(now));
      return r(startOfDay(d), endOfDay(d));
    }

    case "MONTH": {
      if (!isMonthYearValue(value))
        return r(
          firstOfMonth(now.getFullYear(), now.getMonth()),
          lastOfMonth(now.getFullYear(), now.getMonth()),
        );
      const { month, year } = value;
      return r(firstOfMonth(year, month - 1), lastOfMonth(year, month - 1));
    }

    case "QUARTER": {
      if (!isQuarterYearValue(value)) {
        const q = currentQuarterNum(now.getMonth());
        return r(firstOfQuarter(now.getFullYear(), q), lastOfQuarter(now.getFullYear(), q));
      }
      return r(firstOfQuarter(value.year, value.quarter), lastOfQuarter(value.year, value.quarter));
    }

    case "YEAR": {
      const yr = isYearOnlyValue(value) ? value.year : now.getFullYear();
      return r(new Date(yr, 0, 1, 0, 0, 0, 0), endOfDay(new Date(yr, 11, 31)));
    }

    case "RANGE": {
      if (!isDateRangeValue(value)) return null;
      const { from, to } = value;
      if (!from || !to) return null;
      const fromD = new Date(from);
      const toD = new Date(to);
      if (isNaN(fromD.getTime()) || isNaN(toD.getTime())) return null;
      return r(startOfDay(fromD), endOfDay(toD));
    }

    default:
      return null;
  }
}

// ── Value shape narrowing helpers ─────────────────────────────────────────────

export function isNDaysValue(v: FilterRuleValue): v is { n: number } {
  return (
    typeof v === "object" &&
    v !== null &&
    !Array.isArray(v) &&
    "n" in v &&
    typeof (v as { n: unknown }).n === "number"
  );
}

export function isMonthYearValue(v: FilterRuleValue): v is { month: number; year: number } {
  return (
    typeof v === "object" &&
    v !== null &&
    !Array.isArray(v) &&
    "month" in v &&
    "year" in v &&
    !("quarter" in v)
  );
}

export function isQuarterYearValue(
  v: FilterRuleValue,
): v is { quarter: 1 | 2 | 3 | 4; year: number } {
  return typeof v === "object" && v !== null && !Array.isArray(v) && "quarter" in v && "year" in v;
}

export function isYearOnlyValue(v: FilterRuleValue): v is { year: number } {
  return (
    typeof v === "object" &&
    v !== null &&
    !Array.isArray(v) &&
    "year" in v &&
    !("month" in v) &&
    !("quarter" in v)
  );
}

export function isDateRangeValue(v: FilterRuleValue): v is { from: string; to: string } {
  return (
    typeof v === "object" &&
    v !== null &&
    !Array.isArray(v) &&
    "from" in v &&
    "to" in v &&
    !("preset" in v)
  );
}

// ── Compiled ISO range ────────────────────────────────────────────────────────

/**
 * Resolves DatePeriodId to a pair of ISO strings compatible with Bitrix24 REST.
 * Returns null for NONE or unresolvable ranges.
 */
export function resolveDatePeriodToISO(
  id: DatePeriodId,
  value: FilterRuleValue,
  now: Date,
): { from: string; to: string } | null {
  const range = resolveDatePeriodToRange(id, value, now);
  if (!range) return null;
  return {
    from: toLocalISOString(range.start),
    to: toLocalISOString(range.end),
  };
}
